import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { toast } from "sonner";
import { Clock3, CalendarClock, Check, X } from "lucide-react";

const SB = { submitted: "badge badge-pending", approved: "badge badge-approved", rejected: "badge badge-destructive" };

export default function AdjustmentRequests() {
    const [data, setData] = useState(null);
    const load = () => api.get("/adjustments/queue").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const decide = async (kind, a, decision) => {
        let note = "";
        if (decision === "rejected") { note = window.prompt("Reason for rejection:") || ""; if (!note) return; }
        try {
            await api.post(`/adjustments/${kind}/${a.id}/decide`, { decision, note });
            toast.success(decision === "approved"
                ? (kind === "time" ? "Approved — the attendance record has been corrected" : "Approved — the shift assignment updates on the effective date")
                : "Rejected");
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Manila" }) : "—";
    const Actions = ({ kind, a }) => a.status === "submitted" ? (
        <div className="flex gap-1">
            <button className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white flex items-center gap-1" onClick={() => decide(kind, a, "approved")} data-testid={`approve-${kind}-${a.id}`}><Check className="size-3" /> Approve</button>
            <button className="h-7 px-2 text-xs rounded-md bg-destructive text-white flex items-center gap-1" onClick={() => decide(kind, a, "rejected")}><X className="size-3" /></button>
        </div>
    ) : <span className="text-[10px] text-muted-foreground">—</span>;

    return (
        <div>
            <PageHeader title="Adjustment Requests" subtitle="Time corrections write into the actual attendance record (hours + lateness recomputed vs the shift); shift changes re-assign the employee's schedule on the effective date." />
            {data && (
                <div className="mb-3 text-xs"><span className="badge badge-pending">{data.pending} pending</span></div>
            )}

            <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Clock3 className="size-4 text-primary" /> Time adjustments</h3>
            <TableWrap>
                <thead><tr><th>Employee</th><th>Date</th><th>Recorded</th><th>Requested</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                    {(data?.time_adjustments || []).map((a) => (
                        <tr key={a.id} data-row-id={a.id} data-testid={`tadj-${a.id}`}>
                            <td><b>{a.employee_name}</b> <span className="text-xs text-muted-foreground">{a.employee_no}</span></td>
                            <td className="font-mono text-xs">{a.work_date}</td>
                            <td className="font-mono text-xs">{hhmm(a.current_time_in)}–{hhmm(a.current_time_out)}</td>
                            <td className="font-mono text-xs font-semibold">{a.requested_time_in || "—"}–{a.requested_time_out || "—"}</td>
                            <td className="text-xs max-w-[200px]">{a.reason}</td>
                            <td><span className={SB[a.status]}>{a.status}</span></td>
                            <td><Actions kind="time" a={a} /></td>
                        </tr>
                    ))}
                    {data && !data.time_adjustments.length && <tr><td colSpan={7}><Empty title="No time adjustment requests" /></td></tr>}
                </tbody>
            </TableWrap>

            <h3 className="font-display font-semibold mb-2 mt-5 flex items-center gap-2"><CalendarClock className="size-4 text-primary" /> Shift change requests</h3>
            <TableWrap>
                <thead><tr><th>Employee</th><th>From</th><th>To</th><th>Effective</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                    {(data?.schedule_adjustments || []).map((a) => (
                        <tr key={a.id} data-row-id={a.id} data-testid={`sadj-${a.id}`}>
                            <td><b>{a.employee_name}</b> <span className="text-xs text-muted-foreground">{a.employee_no}</span></td>
                            <td className="text-xs">{a.current_shift_name || "—"}</td>
                            <td className="text-xs font-semibold">{a.requested_shift_name} <span className="text-muted-foreground">({a.requested_shift_hours})</span></td>
                            <td className="font-mono text-xs">{a.effective_date}{a.applied && <span className="text-emerald-600 block text-[10px]">applied ✓</span>}</td>
                            <td className="text-xs max-w-[200px]">{a.reason}</td>
                            <td><span className={SB[a.status]}>{a.status}</span></td>
                            <td><Actions kind="schedule" a={a} /></td>
                        </tr>
                    ))}
                    {data && !data.schedule_adjustments.length && <tr><td colSpan={7}><Empty title="No shift change requests" /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
